import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Trophy, ChevronDown } from "lucide-react";

const HEADLINE = "HAUT IST DEINE LEINWAND.".split(" ");
const VIDEO_URL = "https://www.royal-tattoo.de/wp-content/uploads/2025/12/RTG-Team_2025-12.mp4";
const FALLBACK_IMG = "https://images.unsplash.com/photo-1552627019-947c3789ffb5?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";

export default function HeroSection() {
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Video / Image */}
      {!videoError ? (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          onError={() => setVideoError(true)}
          poster={FALLBACK_IMG}
        >
          <source src={VIDEO_URL} type="video/mp4" />
        </video>
      ) : (
        <img
          src={FALLBACK_IMG}
          alt="Royal Tattoo Hero"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]/50" />
      <div className="absolute inset-0 bg-[#0A0A0A]/60" />

      {/* Convention Badge – Top Right */}
      <motion.div
        data-testid="convention-badge"
        className="absolute top-24 right-6 md:right-12 z-10 flex items-center gap-2 bg-[#141414]/90 border border-[#C9A84C]/40 px-4 py-2.5 backdrop-blur-sm"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
      >
        <Trophy size={14} className="text-[#C9A84C]" />
        <span className="text-[#C9A84C] text-xs tracking-[0.12em] font-outfit font-medium">
          6× Convention-Gewinner · Meckenheim
        </span>
      </motion.div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center pb-24">
        <motion.p
          className="section-label mb-6 text-center"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          Premium Tattoo & Piercing Studio
        </motion.p>

        {/* Headline word-by-word */}
        <h1 className="font-cormorant font-bold text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-[#F0F0F0] leading-none mb-6 tracking-tight">
          {HEADLINE.map((word, i) => (
            <motion.span
              key={i}
              className="inline-block mr-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.1, duration: 0.6, ease: "easeOut" }}
            >
              {word}
            </motion.span>
          ))}
        </h1>

        <motion.p
          className="text-[#888888] text-base md:text-lg max-w-xl mx-auto mb-10 font-outfit leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
        >
          Internationale Künstler.{" "}
          <span className="text-[#C9A84C]">Faire Preise.</span> Königliche Qualität.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.6 }}
        >
          <a
            href="#kontakt"
            data-testid="hero-cta-primary"
            className="gold-btn-solid animate-gold-pulse"
          >
            Termin anfragen
          </a>
          <a
            href="#studios"
            data-testid="hero-cta-secondary"
            className="ghost-btn"
          >
            Unsere Studios entdecken
          </a>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.6 }}
      >
        <span className="text-[#888888] text-xs tracking-[0.2em] uppercase">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
        >
          <ChevronDown size={18} className="text-[#C9A84C]" />
        </motion.div>
      </motion.div>
    </section>
  );
}
